import { useState } from "react";
import { useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { Dialog } from "@themes/pollinator/ui/Dialog";
import { useDialog, useVideoMeetingDetails } from "@platform/store";
import { useNavigate } from "react-router-dom";
import { leaveVideoMeeting } from "@core/features/videoMeeting";
import { leaveChimeSession, stopChimeStreams } from "@core/features/video";
import { videoEventBus } from "@core/features/video";

export function VideoMeetingEndDialog() {
  const navigate = useNavigate();
  const { activeDialog, closeDialog } = useDialog();
  const [isLeaving, setIsLeaving] = useState(false);

  const meetingManager = useMeetingManager();
  const { meetingId, roomId, chimeExternalUserId, meetingDetails } = useVideoMeetingDetails();

  const handleLeaveMeeting = async (): Promise<void> => {
    try {
      setIsLeaving(true);

      await stopChimeStreams(meetingManager);

      // Leave the video room API
      if (roomId) {
        try {
          await leaveVideoMeeting(roomId, chimeExternalUserId);
        } catch (_) {
          // Continue even if API call fails
        }
      }

      // Leave the Chime meeting
      if (meetingId) {
        await leaveChimeSession(meetingManager);
        videoEventBus.emitEvent("videochat:left", {
          meetingId: meetingId.toString(),
        });
      }

      // Don't reset the store here — clearing isInitialized triggers
      // VideoMeetingPage's redirect to /video/join, racing with this navigate.
      // The 500ms auto-refresh on VideoEndedPage clears all in-memory state.
      navigate(`/video/end/${meetingDetails?.id}`);
    } catch (err) {
      await stopChimeStreams(meetingManager);
      navigate(`/video/end/${meetingDetails?.id}`);
    } finally {
      setIsLeaving(false);
      closeDialog();
      sessionStorage.removeItem("video_session");
    }
  };

  const isOpen = activeDialog === "endMeeting";

  const handleClose = () => {
    closeDialog();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Leave Meeting"
      description="Are you sure you want to leave the meeting?"
      isWithActions={true}
      primaryButtonText={isLeaving ? "Leaving..." : "Leave Meeting"}
      primaryButtonProps={{
        disabled: isLeaving,
        destructive: true,
      }}
      secondaryButtonText="Cancel"
      onPrimaryClick={handleLeaveMeeting}
      onSecondaryClick={handleClose}
    />
  );
}
